import * as React from "react";
import { Details, Meter, Pill, Section, Stat } from "@ozpanel/ui";

// MailActivity — the shared mail-activity dashboard both the tenant (account) and mailbox consoles
// render from GET /mail/activity and GET /mail/me/activity. One component, so the two consoles show
// the same numbers the same way. Only failures get colour, so a healthy account reads as calm.

export type ActivityOutgoing = {
  sent: number;
  deferred: number;
  rejected: number;
  bounced: number;
  spam: number;
};
export type CounterWindow = { used: number; limit: number; reset_sec: number; available: boolean };
export type MailActivity = {
  scope: string;
  mailbox?: string;
  today: { incoming: number; outgoing: ActivityOutgoing };
  this_hour: {
    incoming: number;
    incoming_available: boolean;
    outgoing: { deferred: number; rejected: number; bounced: number; spam: number };
    sent?: number;
  };
  sending: { hourly?: CounterWindow; daily?: CounterWindow; note?: string };
  forwarders: { total: number; external: number; in_force?: boolean };
  note?: string;
};

function UsageRow({ label, w }: { label: string; w: CounterWindow }) {
  const pct = w.limit > 0 ? Math.round((w.used / w.limit) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between text-[12.5px]">
        <span className="text-muted">{label}</span>
        <span className="tabular">
          {!w.available ? "usage unavailable" : w.limit > 0 ? `${w.used} of ${w.limit}` : `${w.used} sent · no limit`}
        </span>
      </div>
      {w.available && w.limit > 0 && (
        <div className="mt-1.5">
          <Meter pct={pct} tone={pct >= 90 ? "crit" : pct >= 70 ? "warn" : "ok"} />
        </div>
      )}
      {w.available && w.limit > 0 && w.reset_sec > 0 && (
        <div className="mt-1 text-[11.5px] text-muted">Resets in {Math.ceil(w.reset_sec / 60)} min</div>
      )}
    </div>
  );
}

/** One headline figure: a word and a number, side by side, at a size a phone can take in at once. */
function Figure({ label, value, sub, tone }: { label: string; value: number; sub?: string; tone?: "accent" }) {
  return (
    <div className="min-w-0">
      <div className="text-[12.5px] text-muted">{label}</div>
      <div className={"text-[24px] leading-tight font-[680] tabular " + (tone === "accent" ? "text-accent" : "text-fg")}>{value}</div>
      {sub && <div className="text-[12px] text-muted">{sub}</div>}
    </div>
  );
}

/**
 * The mail-activity card both customer consoles open with.
 *
 * It was seven equal tiles in a grid — a whole phone screen of zeros on a quiet day (owner's
 * screenshot, 2026-11-02), with the two numbers people look for (received, sent) given the same
 * weight as five that are only interesting when they are not zero. Now: received and sent as the
 * headline; the problem counts named only when there IS a problem (a calm day reads as one line);
 * the sending allowance with what governs it; and the full breakdown one tap away.
 */
export function MailActivityDashboard({ a, title = "Mail today" }: { a: MailActivity; title?: string }) {
  const th = a.this_hour;
  const o = a.today.outgoing;
  const problems: { label: string; n: number; tone: "warn" | "crit" }[] = [
    { label: "deferred", n: o.deferred, tone: "warn" as const },
    { label: "rejected", n: o.rejected, tone: "crit" as const },
    { label: "bounced", n: o.bounced, tone: "warn" as const },
    { label: "spam blocked", n: o.spam, tone: "warn" as const },
  ].filter((p) => p.n > 0);
  const hourHint = (n: number) => (th.incoming_available && n > 0 ? `${n} this hour` : undefined);
  return (
    <Section title={title}>
      <div className="grid grid-cols-2 gap-4">
        <Figure
          label="Received"
          value={a.today.incoming}
          tone="accent"
          sub={th.incoming_available ? `${th.incoming} this hour` : undefined}
        />
        <Figure label="Sent" value={o.sent} sub={th.sent != null ? `${th.sent} this hour` : undefined} />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[13px]">
        {problems.length === 0 ? (
          <span className="text-muted">No delivery problems today.</span>
        ) : (
          problems.map((p) => (
            <Pill key={p.label} tone={p.tone}>
              {p.n} {p.label}
            </Pill>
          ))
        )}
      </div>

      {(a.sending.hourly || a.sending.daily) && (
        <div className="mt-4 space-y-3">
          {a.sending.hourly && (
            // What GOVERNS the number stays on screen: for a mailbox the allowance is the whole
            // account's, and "0 of 100" read as your own would be planned against wrongly.
            <UsageRow label={a.scope === "mailbox" ? "Sending this hour (shared by the account)" : "Sending this hour"} w={a.sending.hourly} />
          )}
          {a.sending.daily && <UsageRow label={a.scope === "mailbox" ? "Sending today (shared by the account)" : "Sending today"} w={a.sending.daily} />}
        </div>
      )}

      <div className="mt-3 text-[12.5px] text-muted">
        Forwarders: {a.forwarders.total}
        {a.forwarders.in_force === false
          ? " · external forwarding is off"
          : a.forwarders.external > 0
            ? ` · ${a.forwarders.external} to outside addresses`
            : ""}
      </div>

      <Details summary="Every count, today and this hour" className="mt-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Received today" value={a.today.incoming} hint={th.incoming_available ? `${th.incoming} this hour` : "this hour: —"} />
          <Stat label="Sent today" value={o.sent} hint={th.sent != null ? `${th.sent} this hour` : undefined} />
          <Stat label="Deferred today" value={o.deferred} tone={o.deferred > 0 ? "warn" : undefined} hint={hourHint(th.outgoing.deferred)} />
          <Stat label="Rejected today" value={o.rejected} tone={o.rejected > 0 ? "crit" : undefined} hint={hourHint(th.outgoing.rejected)} />
          <Stat label="Bounced today" value={o.bounced} tone={o.bounced > 0 ? "warn" : undefined} hint={hourHint(th.outgoing.bounced)} />
          <Stat label="Spam blocked today" value={o.spam} />
        </div>
        {a.sending.note && <p>{a.sending.note}</p>}
        {a.note && <p>{a.note}</p>}
      </Details>
    </Section>
  );
}
